/**
 * The portal's spreadsheet export: what a field is, and how a chosen set of them becomes
 * an Excel file or a CSV.
 *
 * A screen supplies its own field catalogue and whatever it needs looked up (a context),
 * and gets both file formats and the picker for free. Customers was the first; Leads is
 * the second, and the reason this is not written twice.
 *
 * Excel is the default format because a CSV loses things people then blame on the data:
 * Excel strips the leading zero off a phone number, turns a long ID into 2.7E+10, and
 * reads dates the American way. Every column here is written as what it actually is.
 */

import type { SheetData } from 'write-excel-file/browser';

export type Row = Record<string, unknown>;
export type Cell = string | number | null;

/** What a column holds, so a real date or number is written rather than text. */
export type FieldKind = 'text' | 'number' | 'money' | 'date' | 'dateTime';

export interface ExportField<Ctx> {
  key: string;
  label: string;
  kind: FieldKind;
  value: (row: Row, ctx: Ctx) => Cell;
}

export interface ExportGroup<Ctx> {
  label: string;
  fields: ExportField<Ctx>[];
}

export interface Formatter {
  (v: unknown): Cell;
  kind: FieldKind;
}

export function fmt(kind: FieldKind, fn: (v: unknown) => Cell): Formatter {
  return Object.assign(fn, { kind });
}

export const text = fmt('text', (v) => (v == null || v === '' ? null : String(v)));
export const num = fmt('number', (v) => (v == null || v === '' ? null : Number(v)));
export const money = fmt('money', (v) => (v == null || v === '' ? null : Number(v)));
/** Yes and No rather than TRUE and FALSE: this is read by people, and it filters the same. */
export const yesNo = fmt('text', (v) => (v == null ? null : v ? 'Yes' : 'No'));
export const titleCase = fmt('text', (v) =>
  typeof v === 'string' && v ? v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ') : null);
export const date = fmt('date', (v) => (typeof v === 'string' && v ? v.slice(0, 10) : null));
/** Stored in UTC; the header says so rather than shifting it to a zone the reader may not share. */
export const dateTime = fmt('dateTime', (v) =>
  typeof v === 'string' && v ? v.slice(0, 16).replace('T', ' ') : null);
/** A Postgres array column, which arrives as a list rather than a string. */
export const list = fmt('text', (v) =>
  Array.isArray(v) && v.length > 0 ? v.filter(Boolean).join(', ') : null);

export function field<Ctx>(
  key: string,
  label: string,
  value: ExportField<Ctx>['value'],
  kind: FieldKind = 'text',
): ExportField<Ctx> {
  return { key, label, kind, value };
}

export function column<Ctx>(key: string, label: string, format: Formatter = text): ExportField<Ctx> {
  return field<Ctx>(key, label, (r) => format(r[key]), format.kind);
}

/** One part of a stored address or attribution blob, as its own column. */
export function part(blob: unknown, key: string): Cell {
  if (!blob || typeof blob !== 'object' || Array.isArray(blob)) return null;
  return text((blob as Row)[key]);
}

// ---------------------------------------------------------------- CSV

/** A cell that opens in Excel as the text it is, never as a formula. */
function csvCell(v: Cell): string {
  if (v == null) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(Math.round(v * 100) / 100) : '';
  let s = v;
  // Phone numbers like +27 82 ... are left alone; anything else starting with a formula
  // character is made inert, so a name typed as =HYPERLINK(...) cannot run on open.
  if (/^[=@\t\r]/.test(s) || (/^[+-]/.test(s) && !/^[+-][\d\s()]+$/.test(s))) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildCsv<Ctx>(rows: Row[], fields: ExportField<Ctx>[], ctx: Ctx): string {
  const lines = [fields.map((f) => csvCell(f.label)).join(',')];
  for (const r of rows) {
    lines.push(fields.map((f) => csvCell(f.value(r, ctx))).join(','));
  }
  // The byte-order mark is what makes Excel read æ, ø, å and ë correctly instead of as junk.
  return `\uFEFF${lines.join('\r\n')}`;
}

// ---------------------------------------------------------------- Excel

/** dd/mm/yyyy, as the rest of the portal shows dates. Excel still treats these as dates. */
const DATE_FORMAT = 'dd/mm/yyyy';
const DATE_TIME_FORMAT = 'dd/mm/yyyy hh:mm';
const MONEY_FORMAT = '#,##0.00';

/** A stored yyyy-mm-dd (or yyyy-mm-dd hh:mm) read as itself, not shifted by the browser's zone. */
function toDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value);
  if (!m) return null;
  const [, y, mo, d, hh, mm] = m;
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh ?? 0), Number(mm ?? 0)));
}

/** Wide enough to read without one runaway note stretching a column off the screen. */
function widthFor<Ctx>(f: ExportField<Ctx>): number {
  const label = Math.min(f.label.length + 3, 34);
  if (f.kind === 'dateTime') return Math.max(label, 18);
  if (f.kind === 'date') return Math.max(label, 12);
  if (f.kind === 'money' || f.kind === 'number') return Math.max(label, 13);
  return Math.max(label, 14);
}

export async function downloadExcel<Ctx>(
  rows: Row[],
  fields: ExportField<Ctx>[],
  ctx: Ctx,
  fileName: string,
  sheetName: string,
): Promise<void> {
  // Loaded on the click, never with the page, so the portal carries none of its weight
  // until somebody exports.
  const writeXlsxFile = (await import('write-excel-file/browser')).default;

  const header: SheetData[number] = fields.map((f) => ({ value: f.label, fontWeight: 'bold' as const }));

  const body: SheetData = rows.map((r) =>
    fields.map((f): SheetData[number][number] => {
      const raw = f.value(r, ctx);
      // An empty cell is written as nothing at all, not as an empty string.
      if (raw == null || raw === '') return null;

      if (f.kind === 'date' || f.kind === 'dateTime') {
        const d = typeof raw === 'string' ? toDate(raw) : null;
        // A date we cannot read stays visible as the text it was, rather than vanishing.
        if (!d) return { value: String(raw), type: String };
        return {
          value: d,
          type: Date,
          format: f.kind === 'date' ? DATE_FORMAT : DATE_TIME_FORMAT,
        };
      }

      if (f.kind === 'money' || f.kind === 'number') {
        const n = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(n)) return { value: String(raw), type: String };
        return f.kind === 'money'
          ? { value: n, type: Number, format: MONEY_FORMAT }
          : { value: n, type: Number };
      }

      // Everything else is text on purpose. This is what keeps 082... from losing its zero.
      return { value: String(raw), type: String };
    }),
  );

  const sheetData: SheetData = [header, ...body];

  await writeXlsxFile(sheetData, {
    columns: fields.map((f) => ({ width: widthFor(f) })),
    stickyRowsCount: 1,
    sheet: sheetName,
  }).toFile(fileName);
}

/** Reads a whole table in pages, so a country past the row cap still exports in full. */
export async function fetchAllPages<T>(
  page: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
  what: string,
): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await page(from, from + PAGE - 1);
    if (error) throw new Error(`Could not read ${what}: ${error.message}`);
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}
